import { useState, useEffect, useMemo, useCallback } from 'react';
import { ipSecurityApi } from '../../services/api';
import {
  ShieldBan,
  ShieldAlert,
  ShieldCheck,
  Globe,
  Search,
  Plus,
  Trash2,
  RefreshCw,
  Eye,
  Save,
  Loader2,
  X,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';

// ============================================================================
// Types
// ============================================================================

interface Stats {
  totalBanned: number;
  fail2banBans: number;
  manualBans: number;
  geoBans: number;
  totalHits: number;
  watchedIps: number;
}

interface Fail2BanConfig {
  enabled: boolean;
  maxAttempts: number;
  windowMinutes: number;
  banDurationMinutes: number;
}

interface GeoConfig {
  enabled: boolean;
  mode: 'blocklist' | 'allowlist';
  countries: string[];
}

interface BanEntry {
  id: string;
  ipAddress: string;
  source: 'FAIL2BAN' | 'MANUAL' | 'GEO_BLOCKED';
  description: string | null;
  country: string | null;
  expiresAt: string | null;
  bannedBy: string | null;
  isActive: boolean;
  hitCount: number;
  lastHitAt: string | null;
  createdAt: string;
}

interface LookupResult {
  normalizedIp: string;
  country: string | null;
  isBlocked: boolean;
  reason?: string;
  isWhitelisted: boolean;
}

// ============================================================================
// Country data organized by region
// ============================================================================

const COUNTRY_REGIONS: Record<string, { label: string; countries: { code: string; name: string }[] }> = {
  europe_west: {
    label: 'Europe de l\'Ouest',
    countries: [
      { code: 'FR', name: 'France' }, { code: 'DE', name: 'Allemagne' }, { code: 'BE', name: 'Belgique' },
      { code: 'LU', name: 'Luxembourg' }, { code: 'NL', name: 'Pays-Bas' }, { code: 'CH', name: 'Suisse' },
      { code: 'AT', name: 'Autriche' }, { code: 'GB', name: 'Royaume-Uni' }, { code: 'IE', name: 'Irlande' },
      { code: 'MC', name: 'Monaco' }, { code: 'LI', name: 'Liechtenstein' },
    ],
  },
  europe_south: {
    label: 'Europe du Sud',
    countries: [
      { code: 'ES', name: 'Espagne' }, { code: 'IT', name: 'Italie' }, { code: 'PT', name: 'Portugal' },
      { code: 'GR', name: 'Grèce' }, { code: 'MT', name: 'Malte' }, { code: 'CY', name: 'Chypre' },
      { code: 'HR', name: 'Croatie' }, { code: 'SI', name: 'Slovénie' }, { code: 'AL', name: 'Albanie' },
      { code: 'ME', name: 'Monténégro' }, { code: 'MK', name: 'Macédoine du Nord' },
      { code: 'RS', name: 'Serbie' }, { code: 'BA', name: 'Bosnie-Herzégovine' },
    ],
  },
  europe_north: {
    label: 'Europe du Nord',
    countries: [
      { code: 'SE', name: 'Suède' }, { code: 'NO', name: 'Norvège' }, { code: 'DK', name: 'Danemark' },
      { code: 'FI', name: 'Finlande' }, { code: 'IS', name: 'Islande' },
      { code: 'EE', name: 'Estonie' }, { code: 'LV', name: 'Lettonie' }, { code: 'LT', name: 'Lituanie' },
    ],
  },
  europe_east: {
    label: 'Europe de l\'Est',
    countries: [
      { code: 'PL', name: 'Pologne' }, { code: 'CZ', name: 'Tchéquie' }, { code: 'SK', name: 'Slovaquie' },
      { code: 'HU', name: 'Hongrie' }, { code: 'RO', name: 'Roumanie' }, { code: 'BG', name: 'Bulgarie' },
      { code: 'UA', name: 'Ukraine' }, { code: 'MD', name: 'Moldavie' }, { code: 'BY', name: 'Biélorussie' },
    ],
  },
  russia_cis: {
    label: 'Russie & CEI',
    countries: [
      { code: 'RU', name: 'Russie' }, { code: 'KZ', name: 'Kazakhstan' }, { code: 'UZ', name: 'Ouzbékistan' },
      { code: 'GE', name: 'Géorgie' }, { code: 'AM', name: 'Arménie' }, { code: 'AZ', name: 'Azerbaïdjan' },
      { code: 'KG', name: 'Kirghizistan' }, { code: 'TJ', name: 'Tadjikistan' }, { code: 'TM', name: 'Turkménistan' },
    ],
  },
  north_america: {
    label: 'Amérique du Nord',
    countries: [
      { code: 'US', name: 'États-Unis' }, { code: 'CA', name: 'Canada' }, { code: 'MX', name: 'Mexique' },
    ],
  },
  south_america: {
    label: 'Amérique du Sud',
    countries: [
      { code: 'BR', name: 'Brésil' }, { code: 'AR', name: 'Argentine' }, { code: 'CL', name: 'Chili' },
      { code: 'CO', name: 'Colombie' }, { code: 'PE', name: 'Pérou' }, { code: 'VE', name: 'Venezuela' },
      { code: 'EC', name: 'Équateur' }, { code: 'UY', name: 'Uruguay' }, { code: 'PY', name: 'Paraguay' },
      { code: 'BO', name: 'Bolivie' },
    ],
  },
  middle_east: {
    label: 'Moyen-Orient',
    countries: [
      { code: 'TR', name: 'Turquie' }, { code: 'SA', name: 'Arabie Saoudite' }, { code: 'AE', name: 'Émirats arabes unis' },
      { code: 'IL', name: 'Israël' }, { code: 'IQ', name: 'Irak' }, { code: 'IR', name: 'Iran' },
      { code: 'SY', name: 'Syrie' }, { code: 'JO', name: 'Jordanie' }, { code: 'LB', name: 'Liban' },
      { code: 'KW', name: 'Koweït' }, { code: 'QA', name: 'Qatar' }, { code: 'BH', name: 'Bahreïn' },
      { code: 'OM', name: 'Oman' }, { code: 'YE', name: 'Yémen' },
    ],
  },
  asia_east: {
    label: 'Asie de l\'Est',
    countries: [
      { code: 'CN', name: 'Chine' }, { code: 'JP', name: 'Japon' }, { code: 'KR', name: 'Corée du Sud' },
      { code: 'KP', name: 'Corée du Nord' }, { code: 'TW', name: 'Taïwan' }, { code: 'MN', name: 'Mongolie' },
      { code: 'HK', name: 'Hong Kong' },
    ],
  },
  asia_south: {
    label: 'Asie du Sud',
    countries: [
      { code: 'IN', name: 'Inde' }, { code: 'PK', name: 'Pakistan' }, { code: 'BD', name: 'Bangladesh' },
      { code: 'LK', name: 'Sri Lanka' }, { code: 'NP', name: 'Népal' }, { code: 'AF', name: 'Afghanistan' },
    ],
  },
  asia_southeast: {
    label: 'Asie du Sud-Est',
    countries: [
      { code: 'TH', name: 'Thaïlande' }, { code: 'VN', name: 'Vietnam' }, { code: 'ID', name: 'Indonésie' },
      { code: 'MY', name: 'Malaisie' }, { code: 'PH', name: 'Philippines' }, { code: 'SG', name: 'Singapour' },
      { code: 'MM', name: 'Myanmar' }, { code: 'KH', name: 'Cambodge' }, { code: 'LA', name: 'Laos' },
    ],
  },
  africa_north: {
    label: 'Afrique du Nord',
    countries: [
      { code: 'MA', name: 'Maroc' }, { code: 'DZ', name: 'Algérie' }, { code: 'TN', name: 'Tunisie' },
      { code: 'LY', name: 'Libye' }, { code: 'EG', name: 'Égypte' }, { code: 'SD', name: 'Soudan' },
    ],
  },
  africa_sub: {
    label: 'Afrique subsaharienne',
    countries: [
      { code: 'NG', name: 'Nigéria' }, { code: 'ZA', name: 'Afrique du Sud' }, { code: 'KE', name: 'Kenya' },
      { code: 'GH', name: 'Ghana' }, { code: 'ET', name: 'Éthiopie' }, { code: 'TZ', name: 'Tanzanie' },
      { code: 'CI', name: 'Côte d\'Ivoire' }, { code: 'SN', name: 'Sénégal' }, { code: 'CM', name: 'Cameroun' },
      { code: 'CD', name: 'RD Congo' }, { code: 'UG', name: 'Ouganda' }, { code: 'MZ', name: 'Mozambique' },
    ],
  },
  oceania: {
    label: 'Océanie',
    countries: [
      { code: 'AU', name: 'Australie' }, { code: 'NZ', name: 'Nouvelle-Zélande' },
      { code: 'FJ', name: 'Fidji' }, { code: 'PG', name: 'Papouasie-Nouvelle-Guinée' },
    ],
  },
};

// ============================================================================
// Helper components
// ============================================================================

function StatCard({ label, value, icon: Icon, colorClass }: { label: string; value: number; icon: any; colorClass: string }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={clsx('p-2 rounded-lg', colorClass)}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

function SourceBadge({ source }: { source: string }) {
  const styles: Record<string, string> = {
    FAIL2BAN: 'bg-orange-100 text-orange-700 border-orange-200',
    MANUAL: 'bg-red-100 text-red-700 border-red-200',
    GEO_BLOCKED: 'bg-purple-100 text-purple-700 border-purple-200',
  };
  const labels: Record<string, string> = {
    FAIL2BAN: 'Fail2Ban',
    MANUAL: 'Manuel',
    GEO_BLOCKED: 'Géo',
  };
  return (
    <span className={clsx('text-xs px-2 py-0.5 rounded-full border font-medium', styles[source] || 'bg-gray-100 text-gray-600')}>
      {labels[source] || source}
    </span>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
        checked ? 'bg-cyan-500' : 'bg-gray-300'
      )}
    >
      <span className={clsx(
        'inline-block h-5 w-5 rounded-full bg-white shadow transition-transform',
        checked ? 'translate-x-5' : 'translate-x-0.5'
      )} />
    </button>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export default function IpSecurityPage() {
  const [activeTab, setActiveTab] = useState<'fail2ban' | 'banned' | 'geo' | 'whitelist'>('fail2ban');
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      const res = await ipSecurityApi.getStats();
      setStats(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadStats().finally(() => setLoading(false));
  }, [loadStats]);

  const tabs = [
    { key: 'fail2ban' as const, label: 'Fail2Ban', icon: ShieldAlert },
    { key: 'banned' as const, label: 'IPs Bannies', icon: ShieldBan },
    { key: 'geo' as const, label: 'Géo-blocage', icon: Globe },
    { key: 'whitelist' as const, label: 'Liste blanche', icon: ShieldCheck },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sécurité IP</h1>
          <p className="text-gray-500 text-sm mt-1">Fail2Ban, bannissement IP, géo-blocage et liste blanche</p>
        </div>
        <button
          onClick={() => { setLoading(true); loadStats().finally(() => setLoading(false)); }}
          className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm"
        >
          <RefreshCw className={clsx('w-4 h-4', loading && 'animate-spin')} />
          Actualiser
        </button>
      </div>

      {/* Stat cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="IPs bloquées" value={stats.totalBanned} icon={ShieldBan} colorClass={stats.totalBanned > 0 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'} />
          <StatCard label="Fail2Ban" value={stats.fail2banBans} icon={ShieldAlert} colorClass="bg-orange-100 text-orange-600" />
          <StatCard label="Manuels" value={stats.manualBans} icon={ShieldBan} colorClass="bg-red-100 text-red-600" />
          <StatCard label="Géo-bloqués" value={stats.geoBans} icon={Globe} colorClass="bg-purple-100 text-purple-600" />
          <StatCard label="Hits bloqués" value={stats.totalHits} icon={Eye} colorClass="bg-cyan-100 text-cyan-600" />
          <StatCard label="IPs surveillées" value={stats.watchedIps} icon={Search} colorClass="bg-yellow-100 text-yellow-600" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all flex-1 justify-center',
              activeTab === tab.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            )}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'fail2ban' && <Fail2BanTab onConfigChange={loadStats} />}
      {activeTab === 'banned' && <BannedIpsTab onBanChange={loadStats} />}
      {activeTab === 'geo' && <GeoBlockingTab onConfigChange={loadStats} />}
      {activeTab === 'whitelist' && <WhitelistTab />}
    </div>
  );
}

// ============================================================================
// Fail2Ban Tab
// ============================================================================

function Fail2BanTab({ onConfigChange }: { onConfigChange: () => void }) {
  const [config, setConfig] = useState<Fail2BanConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    ipSecurityApi.getFail2BanConfig().then(res => {
      setConfig(res.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await ipSecurityApi.updateFail2BanConfig(config);
      onConfigChange();
    } catch { /* ignore */ }
    setSaving(false);
  };

  if (!loaded) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!config) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Configuration Fail2Ban</h2>
          <p className="text-sm text-gray-500 mt-1">Bannissement automatique après plusieurs tentatives de connexion échouées</p>
        </div>
        <Toggle checked={config.enabled} onChange={(v) => setConfig({ ...config, enabled: v })} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tentatives max</label>
          <input
            type="number"
            min={1}
            max={100}
            value={config.maxAttempts}
            onChange={(e) => setConfig({ ...config, maxAttempts: parseInt(e.target.value) || 5 })}
            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
          />
          <p className="text-xs text-gray-400 mt-1">Nombre d'échecs avant bannissement</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fenêtre (minutes)</label>
          <input
            type="number"
            min={1}
            max={1440}
            value={config.windowMinutes}
            onChange={(e) => setConfig({ ...config, windowMinutes: parseInt(e.target.value) || 15 })}
            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
          />
          <p className="text-xs text-gray-400 mt-1">Période de comptage des échecs</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Durée du ban (minutes)</label>
          <input
            type="number"
            min={1}
            max={525600}
            value={config.banDurationMinutes}
            onChange={(e) => setConfig({ ...config, banDurationMinutes: parseInt(e.target.value) || 60 })}
            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
          />
          <p className="text-xs text-gray-400 mt-1">Durée du bannissement automatique</p>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Banned IPs Tab
// ============================================================================

function BannedIpsTab({ onBanChange }: { onBanChange: () => void }) {
  const [bans, setBans] = useState<BanEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showBanModal, setShowBanModal] = useState(false);
  const [showLookupModal, setShowLookupModal] = useState(false);
  const [unbanning, setUnbanning] = useState<string | null>(null);

  const loadBans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ipSecurityApi.getBannedIps({ page, limit: 20, search: search || undefined });
      setBans(res.data.data);
      setTotal(res.data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, search]);

  useEffect(() => { loadBans(); }, [loadBans]);

  const handleUnban = async (id: string) => {
    setUnbanning(id);
    try {
      await ipSecurityApi.unbanIp(id);
      await loadBans();
      onBanChange();
    } catch { /* ignore */ }
    setUnbanning(null);
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="space-y-4">
      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher IP, pays, description..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
          />
        </div>
        <button onClick={() => setShowLookupModal(true)} className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm">
          <Eye className="w-4 h-4" /> Tester une IP
        </button>
        <button onClick={() => setShowBanModal(true)} className="flex items-center gap-2 px-3 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors text-sm">
          <Plus className="w-4 h-4" /> Bannir une IP
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-3 text-gray-500 font-medium">IP</th>
                <th className="text-left px-4 py-3 text-gray-500 font-medium">Source</th>
                <th className="text-left px-4 py-3 text-gray-500 font-medium">Pays</th>
                <th className="text-left px-4 py-3 text-gray-500 font-medium hidden md:table-cell">Description</th>
                <th className="text-left px-4 py-3 text-gray-500 font-medium hidden lg:table-cell">Date</th>
                <th className="text-left px-4 py-3 text-gray-500 font-medium hidden lg:table-cell">Expiration</th>
                <th className="text-right px-4 py-3 text-gray-500 font-medium">Hits</th>
                <th className="text-right px-4 py-3 text-gray-500 font-medium w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></td></tr>
              ) : bans.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-gray-400">Aucune IP bannie</td></tr>
              ) : (
                bans.map((ban) => (
                  <tr key={ban.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-900 font-mono text-xs">{ban.ipAddress}</td>
                    <td className="px-4 py-3"><SourceBadge source={ban.source} /></td>
                    <td className="px-4 py-3 text-gray-600">{ban.country || '-'}</td>
                    <td className="px-4 py-3 text-gray-500 hidden md:table-cell max-w-[200px] truncate">{ban.description || '-'}</td>
                    <td className="px-4 py-3 text-gray-500 hidden lg:table-cell text-xs">{new Date(ban.createdAt).toLocaleString('fr-FR')}</td>
                    <td className="px-4 py-3 text-gray-500 hidden lg:table-cell text-xs">
                      {ban.expiresAt ? new Date(ban.expiresAt).toLocaleString('fr-FR') : 'Permanent'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{ban.hitCount}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleUnban(ban.id)}
                        disabled={unbanning === ban.id}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Débannir"
                      >
                        {unbanning === ban.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-xs text-gray-500">{total} résultats</p>
            <div className="flex items-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                className="px-3 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Précédent
              </button>
              <span className="text-xs text-gray-500">{page} / {totalPages}</span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                className="px-3 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Ban Modal */}
      {showBanModal && (
        <BanIpModal
          onClose={() => setShowBanModal(false)}
          onBanned={() => { loadBans(); onBanChange(); setShowBanModal(false); }}
        />
      )}

      {/* Lookup Modal */}
      {showLookupModal && (
        <LookupModal onClose={() => setShowLookupModal(false)} />
      )}
    </div>
  );
}

// ============================================================================
// Ban IP Modal
// ============================================================================

function BanIpModal({ onClose, onBanned }: { onClose: () => void; onBanned: () => void }) {
  const [ipAddress, setIpAddress] = useState('');
  const [description, setDescription] = useState('');
  const [durationMinutes, setDurationMinutes] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ipAddress.trim()) return;
    setSaving(true);
    setError('');
    try {
      await ipSecurityApi.banIp({
        ipAddress: ipAddress.trim(),
        description: description.trim() || undefined,
        durationMinutes: durationMinutes || undefined,
      });
      onBanned();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors du bannissement');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Bannir une IP</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adresse IP ou CIDR</label>
            <input
              type="text"
              value={ipAddress}
              onChange={(e) => setIpAddress(e.target.value)}
              placeholder="192.168.1.100 ou 10.0.0.0/8"
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optionnel)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Raison du bannissement"
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Durée (minutes, 0 = permanent)</label>
            <input
              type="number"
              min={0}
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(parseInt(e.target.value) || 0)}
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
            />
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Annuler</button>
            <button type="submit" disabled={saving || !ipAddress.trim()} className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldBan className="w-4 h-4" />}
              Bannir
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Lookup Modal
// ============================================================================

function LookupModal({ onClose }: { onClose: () => void }) {
  const [ip, setIp] = useState('');
  const [result, setResult] = useState<LookupResult | null>(null);
  const [myIp, setMyIp] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    ipSecurityApi.getMyIp().then(res => setMyIp(res.data)).catch(() => {});
  }, []);

  const lookup = async () => {
    if (!ip.trim()) return;
    setLoading(true);
    try {
      const res = await ipSecurityApi.lookupIp(ip.trim());
      setResult(res.data);
    } catch { setResult(null); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Tester une IP</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        {myIp && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Votre IP vue par le serveur :</p>
            <p className="text-sm font-mono text-cyan-600 font-medium">{myIp.normalizedIp}</p>
            {myIp.xForwardedFor && <p className="text-xs text-gray-400 mt-1">X-Forwarded-For: {myIp.xForwardedFor}</p>}
          </div>
        )}

        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={ip}
            onChange={(e) => setIp(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && lookup()}
            placeholder="Entrez une adresse IP"
            className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
            autoFocus
          />
          <button onClick={lookup} disabled={loading || !ip.trim()} className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors text-sm disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Tester
          </button>
        </div>

        {result && (
          <div className="space-y-3 p-4 rounded-lg border border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2">
              {result.isBlocked ? (
                <ShieldBan className="w-5 h-5 text-red-500" />
              ) : (
                <ShieldCheck className="w-5 h-5 text-green-500" />
              )}
              <span className={clsx('font-semibold', result.isBlocked ? 'text-red-600' : 'text-green-600')}>
                {result.isBlocked ? 'BLOQUÉE' : 'AUTORISÉE'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <div className="text-gray-500">IP normalisée</div>
              <div className="text-gray-900 font-mono">{result.normalizedIp}</div>
              <div className="text-gray-500">Pays</div>
              <div className="text-gray-900">{result.country || 'Inconnu'}</div>
              <div className="text-gray-500">Whitelistée</div>
              <div className={result.isWhitelisted ? 'text-green-600 font-medium' : 'text-gray-600'}>{result.isWhitelisted ? 'Oui' : 'Non'}</div>
              {result.reason && (
                <>
                  <div className="text-gray-500">Raison</div>
                  <div className="text-red-600">{result.reason}</div>
                </>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Fermer</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Geo-blocking Tab
// ============================================================================

function GeoBlockingTab({ onConfigChange }: { onConfigChange: () => void }) {
  const [config, setConfig] = useState<GeoConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());
  const [countrySearch, setCountrySearch] = useState('');

  useEffect(() => {
    ipSecurityApi.getGeoConfig().then(res => {
      setConfig(res.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const selectedCountries = useMemo(() => new Set(config?.countries || []), [config?.countries]);
  const totalCountries = useMemo(() => {
    let count = 0;
    for (const region of Object.values(COUNTRY_REGIONS)) count += region.countries.length;
    return count;
  }, []);

  const toggleCountry = (code: string) => {
    if (!config) return;
    const next = new Set(config.countries);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setConfig({ ...config, countries: Array.from(next) });
  };

  const toggleRegion = (regionKey: string) => {
    if (!config) return;
    const region = COUNTRY_REGIONS[regionKey];
    const codes = region.countries.map(c => c.code);
    const allSelected = codes.every(c => selectedCountries.has(c));
    const next = new Set(config.countries);
    if (allSelected) {
      codes.forEach(c => next.delete(c));
    } else {
      codes.forEach(c => next.add(c));
    }
    setConfig({ ...config, countries: Array.from(next) });
  };

  const selectAll = () => {
    if (!config) return;
    const all: string[] = [];
    for (const region of Object.values(COUNTRY_REGIONS)) {
      region.countries.forEach(c => all.push(c.code));
    }
    setConfig({ ...config, countries: all });
  };

  const selectNone = () => {
    if (!config) return;
    setConfig({ ...config, countries: [] });
  };

  const toggleRegionExpand = (key: string) => {
    const next = new Set(expandedRegions);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpandedRegions(next);
  };

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await ipSecurityApi.updateGeoConfig(config);
      onConfigChange();
    } catch { /* ignore */ }
    setSaving(false);
  };

  if (!loaded) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!config) return null;

  const filteredRegions = countrySearch
    ? Object.entries(COUNTRY_REGIONS).map(([key, region]) => ({
        key,
        ...region,
        countries: region.countries.filter(c =>
          c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
          c.code.toLowerCase().includes(countrySearch.toLowerCase())
        ),
      })).filter(r => r.countries.length > 0)
    : Object.entries(COUNTRY_REGIONS).map(([key, region]) => ({ key, ...region }));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Géo-blocage</h2>
          <p className="text-sm text-gray-500 mt-1">Bloquer ou autoriser les connexions par pays</p>
        </div>
        <Toggle checked={config.enabled} onChange={(v) => setConfig({ ...config, enabled: v })} />
      </div>

      {/* Mode selection */}
      <div className="flex flex-col sm:flex-row gap-3">
        <label className={clsx(
          'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
          config.mode === 'blocklist' ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:border-gray-300'
        )}>
          <input
            type="radio"
            checked={config.mode === 'blocklist'}
            onChange={() => setConfig({ ...config, mode: 'blocklist' })}
            className="text-cyan-500 focus:ring-cyan-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-900">Blocklist</span>
            <p className="text-xs text-gray-500">Bloquer les pays sélectionnés</p>
          </div>
        </label>
        <label className={clsx(
          'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
          config.mode === 'allowlist' ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:border-gray-300'
        )}>
          <input
            type="radio"
            checked={config.mode === 'allowlist'}
            onChange={() => setConfig({ ...config, mode: 'allowlist' })}
            className="text-cyan-500 focus:ring-cyan-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-900">Allowlist</span>
            <p className="text-xs text-gray-500">N'autoriser que les pays sélectionnés</p>
          </div>
        </label>
      </div>

      {/* Search + select all/none */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={countrySearch}
            onChange={(e) => setCountrySearch(e.target.value)}
            placeholder="Rechercher un pays..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
          />
        </div>
        <span className="text-sm text-gray-500 font-medium">{selectedCountries.size} / {totalCountries}</span>
        <button onClick={selectAll} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Tout</button>
        <button onClick={selectNone} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Rien</button>
      </div>

      {/* Country regions accordion */}
      <div className="space-y-2 max-h-[500px] overflow-y-auto">
        {filteredRegions.map((region) => {
          const regionCodes = region.countries.map(c => c.code);
          const selectedInRegion = regionCodes.filter(c => selectedCountries.has(c)).length;
          const allSelected = selectedInRegion === regionCodes.length;
          const someSelected = selectedInRegion > 0 && !allSelected;
          const isExpanded = expandedRegions.has(region.key);

          return (
            <div key={region.key} className="border border-gray-200 rounded-lg overflow-hidden">
              <div
                className="flex items-center gap-3 px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => toggleRegionExpand(region.key)}
              >
                {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={(e) => { e.stopPropagation(); toggleRegion(region.key); }}
                  onClick={(e) => e.stopPropagation()}
                  className="rounded border-gray-300 text-cyan-500 focus:ring-cyan-500"
                />
                <span className="text-sm text-gray-900 font-medium flex-1">{region.label}</span>
                <span className={clsx(
                  'text-xs font-medium px-2 py-0.5 rounded-full',
                  selectedInRegion > 0 ? 'bg-cyan-100 text-cyan-700' : 'bg-gray-200 text-gray-500'
                )}>
                  {selectedInRegion}/{regionCodes.length}
                </span>
              </div>
              {isExpanded && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1 p-3 border-t border-gray-100">
                  {region.countries.map((country) => (
                    <label key={country.code} className={clsx(
                      'flex items-center gap-2 px-2.5 py-1.5 rounded-md cursor-pointer transition-colors',
                      selectedCountries.has(country.code) ? 'bg-cyan-50' : 'hover:bg-gray-50'
                    )}>
                      <input
                        type="checkbox"
                        checked={selectedCountries.has(country.code)}
                        onChange={() => toggleCountry(country.code)}
                        className="rounded border-gray-300 text-cyan-500 focus:ring-cyan-500"
                      />
                      <span className="text-xs font-mono text-gray-500">{country.code}</span>
                      <span className="text-xs text-gray-700 truncate">{country.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Whitelist Tab
// ============================================================================

function WhitelistTab() {
  const [cidrs, setCidrs] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    ipSecurityApi.getWhitelist().then(res => {
      setCidrs((res.data.cidrs || []).join('\n'));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const lines = cidrs.split('\n').map(l => l.trim()).filter(Boolean);
      await ipSecurityApi.updateWhitelist({ cidrs: lines });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { /* ignore */ }
    setSaving(false);
  };

  if (!loaded) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Liste blanche</h2>
        <p className="text-sm text-gray-500 mt-1">
          Les IPs/CIDR dans cette liste ne seront jamais bloquées. 127.0.0.1 et ::1 sont toujours inclus.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Adresses IP / CIDR (une par ligne)</label>
        <textarea
          value={cidrs}
          onChange={(e) => setCidrs(e.target.value)}
          rows={12}
          placeholder={"192.168.1.0/24\n10.0.0.1\n172.16.0.0/12"}
          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 font-mono text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20 resize-y"
        />
        <p className="text-xs text-gray-400 mt-1">{cidrs.split('\n').filter(l => l.trim()).length} entrées</p>
      </div>

      <div className="flex items-center justify-end gap-3">
        {saved && <span className="text-green-600 text-sm font-medium">Enregistré !</span>}
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}
